import { Briefcase } from "lucide-react";

import { customerKey } from "@/hooks/useCustomers";
import { useAdvisorScopedClient } from "@/lib/advisor-nav";
import type { ModuleDefinition, ModuleSection } from "@/lib/module-registry";
import { APP_ROUTES, type AppRoute, type TabRoute } from "@/lib/routes";
import { queryClient } from "@/lib/query";
import { CLIENTS_PATH } from "@/lib/record-paths";
import { isCcaClient } from "@/pages/customers/cca-details/isCcaClient";
import { CUSTOMER_TAB_ROUTES } from "@/pages/customers/customer-tabs";
import { ENERGY_ADVISOR_CUSTOMER_TAB_ROUTES } from "@/pages/energy-advisor/customer-tabs";
import type { Customer } from "@/types/customers";

/** The client tab that shows only for a CCA client (AB#7087). */
const CCA_TAB_PATH = "cca";

/**
 * Whether a client row applies to the client in scope. CCA Details depends on
 * the client's *data*, its segments, not on the caller's permissions, so it
 * reads the client record from the query cache: `useAdvisorScopedClient`
 * fetches that record for every client in scope and re-renders the sidebar
 * when it lands (AB#8740). Until it lands the row stays hidden, so it never
 * shows for a client it doesn't apply to.
 */
function advisorClientRowApplies(clientId: string, segment: string): boolean {
  if (segment !== CCA_TAB_PATH) return true;
  return isCcaClient(queryClient.getQueryData<Customer>(customerKey(clientId)));
}

/**
 * CCA Details sits between Contacts and Bills in the sidebar (AB#8740). Only
 * the nav rows move: the tab array keeps its own order, because the client
 * page's own tab strip renders it.
 */
function withCcaAfterContacts(rows: AppRoute[]): AppRoute[] {
  const cca = rows.find((r) => r.path === CCA_TAB_PATH);
  const rest = rows.filter((r) => r !== cca);
  const contacts = rest.findIndex((r) => r.path === "contacts");
  if (!cca || contacts === -1) return rows;
  return [...rest.slice(0, contacts + 1), cca, ...rest.slice(contacts + 1)];
}

/**
 * The client-scoped nav items, derived from the tab declarations rather than
 * re-listed.
 *
 * The tab array stays the source of truth for path / label / icon / gates —
 * it's what actually registers the routes (as `children` of
 * `/energy-advisor/:customerId`). Deriving means a new advisor tool appears
 * in the sidebar from the same declaration that registers its route.
 *
 * `element` is dropped: these entries are nav-only. The elements are already
 * mounted by the tab routes, and carrying them here would imply this section
 * registers routes — it doesn't (see `sectionRoutes`).
 */
const navRowsFromTabs = (tabs: ReadonlyArray<TabRoute>): AppRoute[] =>
  tabs
    // A disabled tab is a placeholder for something unported — it registers
    // no route, so a nav row pointing at it would dead-end.
    .filter((tab) => !tab.disabled)
    .map((tab) => ({
      path: tab.path,
      label: tab.label,
      icon: tab.icon,
      status: "live" as const,
      element: <></>,
      requiredFeature: tab.requiredFeature,
      requiredPermission: tab.requiredPermission,
      // A tab that owns sub-routes (`nested`) or has deeper pages of its own
      // (`activeOnSubpaths`) keeps its row lit there too. Rows carry no
      // `children`, so without this they'd match exactly and go dark.
      activeOnSubpaths: tab.activeOnSubpaths || tab.nested,
      alsoActiveOn: tab.alsoActiveOn,
    }));

const ENERGY_ADVISOR_CLIENT_NAV_ROUTES: AppRoute[] = navRowsFromTabs(
  ENERGY_ADVISOR_CUSTOMER_TAB_ROUTES,
);

/**
 * The client record's own tabs as nav rows — the second half of the client
 * group, under `/customers/:id/*`.
 *
 * Derived from `CUSTOMER_TAB_ROUTES` for the same reason as the advisor half:
 * that array registers the routes and carries the gates, so deriving means a
 * new client tab appears here automatically and can't drift from what
 * actually exists.
 */
const CLIENT_RECORD_NAV_ROUTES: AppRoute[] = withCcaAfterContacts(
  navRowsFromTabs(CUSTOMER_TAB_ROUTES),
);

/**
 * Energy Advisor — the advisor workspace: RFQs, bid requests, and the
 * advisor-facing client walkthrough.
 *
 * Formerly "Sales". Renamed because the module is the advisor's own
 * workspace, not the whole sales org: the back-office pages it used to carry
 * (Matrix Pricing, Payees, Supplier Replies, Tasks, plus the Suppliers and
 * Utilities records) moved to the Operations module, and Deals moved to the
 * cross-module record bar. What's left is what an energy advisor actually
 * works in.
 *
 * Its route entries still live in `lib/routes.tsx` (`APP_ROUTES`) rather than
 * being copied here. That file holds ~24 lazy page imports plus their
 * entries, and hand-relocating them would be a large, purely mechanical diff
 * with real transcription risk for zero behavior change. Instead this
 * definition *selects* from `APP_ROUTES` by section, which keeps one source
 * of truth and lets the section list below read as the module's actual nav.
 *
 * New pages can go either place — an entry in `APP_ROUTES` with the matching
 * `section`, or inline in a section's `routes` here. Prefer inline for
 * anything new; `APP_ROUTES` is the legacy home and is expected to shrink as
 * pages move into shared records or their own modules.
 *
 * **Records are not listed as Energy Advisor routes.** Clients, Accounts, and
 * Deals come from shared record declarations (`lib/nav-records.tsx`) and
 * render in the horizontal `RecordBar`, which this module and Operations
 * opt in to (`recordBar`). No module owns them.
 */

/** Entries from `APP_ROUTES` carrying a given legacy section key. */
function legacySection(section: string): AppRoute[] {
  return APP_ROUTES.filter(
    (r) => r.section === section && !r.public && r.status === "live",
  );
}

/**
 * Sectionless, non-public routes — detail pages, drilldowns, redirects, and
 * the bare launcher. Registered with the router but kept out of the sidebar.
 *
 * **Deliberately owned by this module alone, not split with Operations.**
 * These carry no `section`, so there's nothing to key a split on; and they
 * only need to register *once* (`router.tsx` dedupes by path anyway). Their
 * `moduleKey` stamp decides which chrome a cold deep link renders, and the
 * advisor workspace is the right default — it's where the bulk of them live
 * (RFQ walkthroughs, deal drilldowns, Maxwell). Operations' own hidden
 * routes (Matrix Pricing's Capture Health, Capture, and Mapping pages) are
 * reached by drilling in from a page that already resolved Operations
 * chrome, so nav context carries them.
 *
 * Excludes the shared-record detail routes, which register once globally
 * (`recordRoutes()`) rather than per module.
 */
function legacyHiddenRoutes(): AppRoute[] {
  return APP_ROUTES.filter(
    (r) => !r.section && !r.public && r.status === "live",
  );
}

// The product-wide records (Accounts, Clients, Deals) are deliberately NOT a
// section here. They used to be, labeled "Records" — but rendering them as a
// peer of "Workspace" beneath the module pill implied this module owned them,
// when they're shared records no module owns. They live in the horizontal
// `RecordBar` under the topbar now, outside any module's sidebar.
// See `components/layout/RecordBar.tsx`.
const SECTIONS: ModuleSection[] = [
  {
    // Unlabeled leading group. Labeling it "Workspace" produced a
    // "Workspace › Energy Advisor" pair directly under a module pill already
    // reading "Energy Advisor" — three words for one destination. The header
    // earns its keep once this section holds several entries (RFQs appears
    // here for supplier users, and more advisor surfaces are expected); give
    // it the label back then.
    key: "workspace",
    routes: legacySection("workspace"),
  },
  {
    // The selected client's tools, headed by the client's name.
    //
    // These are the children of `/energy-advisor/:customerId`. Every one is
    // client-scoped, which is exactly why they couldn't live in a static
    // sidebar: the module's only static entry was "Energy Advisor" — a nav
    // item duplicating the module's own name, leading to a client picker,
    // with the actual tools one level down and invisible to the nav.
    //
    // `paths` are relative segments; `ModuleSidebar` resolves them against
    // the selected client (`recordScope.useSelection`). Order is written
    // rather than A–Z: this is the advisor's workflow order — profile the
    // client, analyze their bills and contracts, review the snapshot, then
    // solicit. `preserveOrder` opts out of the alphabetical default for
    // exactly this case.
    key: "advisor-client",
    preserveOrder: true,
    recordScope: {
      basePath: "/energy-advisor",
      useScope: useAdvisorScopedClient,
      pickerPlaceholder: "Select a client",
      filterRows: advisorClientRowApplies,
      // Headed "Energy Advisor" / "Client" so it's plain which rows are this
      // module's tools and which are the client record's own tabs — the same
      // client either way, but different surfaces (the record tabs also open
      // from every other module's record bar).
      label: "Energy Advisor",
      // The client record's own tabs — the other half of the advisor's loop.
      // Bills and Contracts are where documents get uploaded; Bill Analyzer
      // and Contract Analyzer above are where they get read.
      secondary: {
        label: "Client",
        basePath: CLIENTS_PATH,
        routes: CLIENT_RECORD_NAV_ROUTES,
      },
    },
    routes: ENERGY_ADVISOR_CLIENT_NAV_ROUTES,
  },
  {
    // Portal Links and Scoring Model — knobs on the advisor's own workflow.
    // Renders behind the sidebar footer's gear, not as a nav section, per the
    // `-settings` suffix convention (`SETTINGS_SECTION_SUFFIX`).
    key: "energy-advisor-settings",
    label: "Settings",
    routes: legacySection("energy-advisor-settings"),
  },
];

export const ENERGY_ADVISOR_MODULE: ModuleDefinition = {
  key: "energy-advisor",
  label: "Energy Advisor",
  icon: Briefcase,
  // The advisor landing, not `/deals`. Deals moved to the cross-module record
  // bar, so landing there would put the user on a page this module's sidebar
  // has no entry for — no active row, and the chrome would resolve from
  // sticky nav context rather than from the switch they just made. Per the "a
  // module's `entryPath` need not be the alphabetically-first item" rule in
  // `pilot-web/CLAUDE.md`: sort the list, choose the landing deliberately.
  entryPath: "/energy-advisor",
  // Binary module access, not the scoped `energy_advisor.view` that gates the
  // advisor workspace: the module also carries client tools and the supplier
  // RFQ portal, used by roles that hold no advisor workspace.
  requiredPermission: "energy_advisor.access",
  // Advisors work Accounts, Clients and Deals all day, so the record bar
  // stays (AB#8790).
  recordBar: true,
  sections: SECTIONS,
  hiddenRoutes: legacyHiddenRoutes(),
};
